import { useState, useCallback, useMemo } from 'react'
import { generateClient } from 'aws-amplify/api'

import {
  getTeam,
  getTeams as getTeamsQuery,
  getTeamByOfferingId as getTeamByOfferingIdQuery,
  getTeamsByOfferingId as getTeamsByOfferingIdQuery,
} from '../graphql/queries'
import {
  refreshTeamCode as refreshTeamCodeMutation,
  cognitoUpdateUser as cognitoUpdateUserMutation,
  updateTeam as updateTeamMutation,
  addTeam as addTeamMutation,
  readyToAdvance as readyToAdvanceMutation,
} from '../graphql/mutations'

export const useTeam = () => {
  const client = useMemo(() => generateClient(), [])
  const [team, setTeam] = useState(null)
  const [teamLoading, setTeamLoading] = useState(false)

  const fetchTeam = useCallback(
    async (id) => {
      setTeamLoading(true)
      return client
        .graphql({
          query: getTeam,
          variables: { id },
        })
        .then((queryResult) => {
          setTeam(queryResult.data.getTeam)
          return queryResult.data.getTeam
        })
        .catch((err) => {
          throw err
        })
        .finally(() => {
          setTeamLoading(false)
        })
    },
    [client],
  )

  const getTeams = useCallback(async () => {
    return client.graphql({ query: getTeamsQuery })
  }, [client])

  const refreshTeamCode = useCallback(
    async (hostNumber, offeringId, teamId) => {
      if (hostNumber === -1) {
        throw new Error('Invalid host number')
      }
      if (offeringId === undefined) {
        throw new Error('Invalid offering id')
      }
      if (teamId === undefined) {
        throw new Error('Invalid team id')
      }
      return client.graphql({
        query: refreshTeamCodeMutation,
        variables: { input: { hostNumber, offeringId, teamId } },
      })
    },
    [client],
  )

  const updateTeamCodeInCognito = useCallback(
    (groupName, userId, newAccessCode) => {
      const userName = `${groupName}${userId}`

      return client.graphql({
        query: cognitoUpdateUserMutation,
        variables: { input: { userName, newAccessCode } },
      })
    },
    [client],
  )

  const updateTeam = useCallback(
    (teamInput) => {
      return client.graphql({
        query: updateTeamMutation,
        variables: { input: { ...teamInput } },
      })
    },
    [client],
  )

  const addTeam = useCallback(
    (offeringId, hostNumber) => {
      return client.graphql({
        query: addTeamMutation,
        variables: { input: { offeringId, hostNumber } },
      })
    },
    [client],
  )

  /**
   * Team indicates they're ready to advance to the next Sim year
   */
  const readyToAdvance = useCallback(
    (offeringId, teamId) => {
      return client.graphql({
        query: readyToAdvanceMutation,
        variables: { input: { offeringId, teamId } },
      })
    },
    [client],
  )

  const getTeamByOfferingId = useCallback(
    (offeringId, id) => {
      return client.graphql({
        query: getTeamByOfferingIdQuery,
        variables: { offeringId, id },
      })
    },
    [client],
  )

  const getTeamsByOfferingId = useCallback(
    (offeringId) => {
      return client.graphql({
        query: getTeamsByOfferingIdQuery,
        variables: { offeringId },
      })
    },
    [client],
  )

  return {
    team,
    teamLoading,
    getTeams,
    fetchTeam,
    refreshTeamCode,
    updateTeamCodeInCognito,
    updateTeam,
    addTeam,
    readyToAdvance,
    getTeamByOfferingId,
    getTeamsByOfferingId,
  }
}
